import React, { useRef, useEffect, useState, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ResizableBox } from 'react-resizable';
import { xmLocalStorage } from '@xmatters/common/utils/xmStorage';
import useResizeObserver from 'hooks/useResizeObserver';
import { getIsHelpDrawerOpen } from 'selectors/helpView';

import GWTMessageSidePanel from 'components/GWTMessageSidePanel';

import appShellStyles from 'Application/ApplicationShell/ApplicationShell.module.scss';
import styles from './SidePanel.module.scss';

const getIsNavBarFullSize = () =>
  JSON.parse(xmLocalStorage.getItem('xm.AllNavWasToggled')) ?? true;

const getIsHelpDrawerDocked = () =>
  JSON.parse(xmLocalStorage.getItem('xm_isHelpMenuDocked')) ?? true;

const EmptyComponent = () => null;

export default function SidePanel({
  content,
  isOpen,
  containerClassName,
  setSidePanelWidth,
  sidePanelWidth,
}) {
  const { resizable } = content ?? {};

  const isHelpDrawerOpen = useSelector(getIsHelpDrawerOpen);

  const mainContentAreaRef = useRef(null);
  const resizableBoxRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isSidePanelFullWidth, setIsSidePanelFullWidth] = useState(false);
  const [sidePanelTopValue, setSidePanelTopValue] = useState(null);
  const [maxSidePanelWidth, setMaxSidePanelWidth] = useState(0);
  const [minSidePanelWidth, setMinSidePanelWidth] = useState(0);
  const [isNavBarFullSize, setIsNavBarFullSize] = useState(
    getIsNavBarFullSize(),
  );
  const [isHelpDrawerDocked, setIsHelpDrawerDocked] = useState(
    getIsHelpDrawerDocked(),
  );

  // Grab the width of the main content area and use it to set
  // the maximum size for the side panel when DOM is ready.
  useEffect(() => {
    mainContentAreaRef.current = document.querySelector(
      // NOTE: Easy to miss, but there's a period in front of this classname,
      // in case you're changing up the selector.
      `.${appShellStyles.rightContent}`,
    );
    setMaxSidePanelWidth(mainContentAreaRef.current.offsetWidth);
    setSidePanelWidth(minSidePanelWidth);
  }, [minSidePanelWidth, setSidePanelWidth]);

  // Toggle side panel width between full size and default size. This
  // gets passed as a prop to the child SidePanel component.
  const toggleMinMaxSidePanelWidth = () => {
    const maxPossibleSidePanelWidth = mainContentAreaRef.current.offsetWidth;

    setSidePanelWidth(
      isSidePanelFullWidth ? minSidePanelWidth : maxPossibleSidePanelWidth,
    );
    setIsSidePanelFullWidth(!isSidePanelFullWidth);
  };

  // When this function is fired, if the nav bar size value
  // in `isNavBarFullSize` is not the same as localstorage,
  // update the state value which, in turn, will run a useEffect.
  const handleContentAreaSizeChange = () => {
    const navBarMinMaxValueInLocalStorage = getIsNavBarFullSize();
    if (navBarMinMaxValueInLocalStorage !== isNavBarFullSize) {
      setIsNavBarFullSize(navBarMinMaxValueInLocalStorage);
    }
  };

  // When `isNavBarFullSize` changes, update `maxSidePanelWidth`.
  useEffect(() => {
    setMaxSidePanelWidth(mainContentAreaRef.current.offsetWidth);
  }, [isNavBarFullSize]);

  // When `maxSidePanelWidth` changes, if side panel is full width,
  // update its width accordingly.
  useEffect(() => {
    if (isSidePanelFullWidth) {
      setSidePanelWidth(maxSidePanelWidth);
    }
  }, [isSidePanelFullWidth, maxSidePanelWidth, setSidePanelWidth]);

  // When `isSidePanelFullWidth` changes, if `true`, grab the main content
  // area's `offsetTop` value and set the side panel's CSS `top` property
  // to match, as the panel is now be `position: fixed`. If `false`,
  // remove the value as the panel is now `position: absolute`.
  useEffect(() => {
    if (isSidePanelFullWidth) {
      const mainContentTopOffset =
        mainContentAreaRef.current.getBoundingClientRect().top;

      setSidePanelTopValue(`${mainContentTopOffset}px`);
    } else {
      setSidePanelTopValue(null);
    }
  }, [isSidePanelFullWidth]);

  const sidePanelRef = useRef();
  const [isAnimating, setIsAnimating] = useState(false);
  const onTransitionStart = useCallback(event => {
    if (
      event.target === sidePanelRef.current &&
      event.propertyName === 'transform'
    ) {
      setIsAnimating(true);
    }
  }, []);
  const onTransitionEnd = useCallback(event => {
    if (
      event.target === sidePanelRef.current &&
      event.propertyName === 'transform'
    ) {
      window.dispatchEvent(new Event('resize'));
      setIsAnimating(false);
    }
  }, []);
  useEffect(() => {
    const sidePanelNode = sidePanelRef.current;
    sidePanelNode.addEventListener('transitionstart', onTransitionStart);
    sidePanelNode.addEventListener('transitionend', onTransitionEnd);
    return () => {
      sidePanelNode.removeEventListener('transitionend', onTransitionEnd);
      sidePanelNode.removeEventListener('transitionstart', onTransitionStart);
    };
  }, [onTransitionStart, onTransitionEnd]);

  // When the main content area is resized, by min/maxing
  // the nav bar or resizing the browser...
  useResizeObserver(mainContentAreaRef, () => {
    if (resizable) {
      // Get the main content area's current width...
      const currentMaxAvailableSidePanelWidth =
        mainContentAreaRef.current.offsetWidth;

      // Handle the changing of available size as per usual...
      setMaxSidePanelWidth(currentMaxAvailableSidePanelWidth);
      setIsHelpDrawerDocked(getIsHelpDrawerDocked());
      handleContentAreaSizeChange();
      // If the panel is open, NOT full width, we're not dragging its resize handle and
      // it's bigger than the space currently available (ie: we're resizing the browser),
      // set it to full width mode and update `sidePanelWidth`, so its size is correct when
      // the user clicks the handle to resize.
      if (
        isOpen &&
        !isSidePanelFullWidth &&
        !isDragging &&
        resizableBoxRef.current?.state.width >=
          currentMaxAvailableSidePanelWidth
      ) {
        setSidePanelWidth(currentMaxAvailableSidePanelWidth);
        setIsSidePanelFullWidth(true);
      }
    }
  });

  // When the user clicks the handle, set `isDragging` to `true`
  // until the `onResizeStop` event is called.
  const handleResizeStart = () => {
    setIsDragging(true);
    handleContentAreaSizeChange();

    // If the side panel is currently full width, set `isSidePanelFullWidth`
    // to false, removing the `position: fixed` class and allowing us to
    // freely manipulate its width. Also, set its width value to the current
    // max available width to avoid unsightly jumps.
    if (isSidePanelFullWidth) {
      setSidePanelWidth(mainContentAreaRef.current.offsetWidth);
      setIsSidePanelFullWidth(false);
    }
  };

  // When we stop resizing, set `isDragging` to `false`, determine whether the
  // side panel is full width and update `isSidePanelFullWidth` accordingly.
  const handleResizeStop = () => {
    setIsDragging(false);
    setSidePanelWidth(resizableBoxRef.current.state.width);
    setIsSidePanelFullWidth(
      resizableBoxRef.current.state.width === maxSidePanelWidth,
    );
  };

  // The minimum width of the side panel is whatever it was initialized
  // as. This is the same functionality as before, just moved into function
  // now that Redux's panelWidth is being updated properly.
  useEffect(() => {
    if (isOpen) {
      setMinSidePanelWidth(sidePanelWidth);
    }
  }, [isOpen]);

  // Reset `sidePanelWidth`, `ideBarTopValue` and `isSidePanelFullWidth` states on close.
  useEffect(() => {
    if (!isOpen) {
      setSidePanelWidth(minSidePanelWidth);
      setSidePanelTopValue(null);
      setIsSidePanelFullWidth(false);
    }
  }, [isNavBarFullSize, isOpen, minSidePanelWidth, setSidePanelWidth]);

  const {
    id,
    component: ComponentProperty,
    containerClassName: altContainerClassName,
    ...otherProps
  } = content ?? {
    component: EmptyComponent,
  };

  // COR-76625
  // As we can now open messages from the dashboard, we'll need to know if the current
  // content is a dashboard message and, if so, render a HOC that wraps the `message`/
  // `scenario` content panels and provides props that can't be gotten from GWT
  const ContentComponent = otherProps.isGWTForm
    ? GWTMessageSidePanel
    : ComponentProperty;

  return (
    <aside
      className={classNames(
        styles.container,
        containerClassName,
        altContainerClassName,
        {
          [styles.hidden]: !isOpen,
          [styles.resizableContainer]: resizable,
        },
      )}
      ref={sidePanelRef}
      data-testid={isOpen && !isAnimating ? 'side-panel-ready' : null}
    >
      {isOpen && (
        <ResizableBox
          axis={resizable ? 'x' : 'none'}
          className={classNames(styles.resizableBox, {
            [styles.isFullWidth]: isSidePanelFullWidth,
            [styles.fullWidthWithMinNav]:
              isSidePanelFullWidth && !isNavBarFullSize,
            [styles.fullWidthWithHelpDrawer]:
              isSidePanelFullWidth && isHelpDrawerOpen && isHelpDrawerDocked,
          })}
          width={sidePanelWidth}
          height={mainContentAreaRef.current?.clientHeight}
          maxConstraints={[maxSidePanelWidth]}
          minConstraints={[minSidePanelWidth]}
          handle={
            resizable && (
              <div
                className={styles.handle}
                data-testid="side-panel-resize-handle"
              />
            )
          }
          resizeHandles={['w']}
          ref={resizableBoxRef}
          onResizeStop={handleResizeStop}
          onResizeStart={handleResizeStart}
          style={{ top: sidePanelTopValue }}
          data-testid="side-panel-resizable-box"
        >
          <div className={styles.content} key={id}>
            <ContentComponent
              {...{
                ...otherProps,
                isFullWidth: isSidePanelFullWidth,
                toggleMinMaxSidePanelWidth,
                resizable,
              }}
              forwardRef={sidePanelRef}
            />
          </div>
        </ResizableBox>
      )}
    </aside>
  );
}

SidePanel.displayName = 'SidePanel';
SidePanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  containerClassName: PropTypes.string,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    component: PropTypes.elementType,
    data: PropTypes.object,
  }),
};
SidePanel.defaultProps = {
  content: null,
  containerClassName: null,
};
